import { useState } from "react";
import { Timeout } from "@lifeat/shared/util/TypeUtil";
import { useEvent } from "./useEvent";

type Handler<TArgs extends any[], TReturn> = (...args: TArgs) => TReturn;

/**
 * Create a debounced event handler that delays the handler invocation by `delayMs`.
 *
 * If the handler is invoked again during the delay,
 * it will cancel the previous invocation and reset the delay timer.
 *
 * @param handler event handler to be debounced.
 * @param delayMs Debounce delay in milliseconds.
 */
export function useDebounce<TArgs extends any[]>(handler: Handler<TArgs, void>, delayMs: number): Handler<TArgs, void> {
    const [timeoutHandle, setTimeoutHandle] = useState<Timeout | null>(null);
    const debounced = useEvent((...args: TArgs) => {
        clearTimeout(timeoutHandle);
        setTimeoutHandle(setTimeout(() => handler(...args), delayMs));
    });

    return debounced;
}
